@use '@/styles/tokens' as *;

// Figma node 232:355173 — 378×150 tile, 17px inset, 25px corner, with the power disc sitting in a
// scoop cut out of the bottom-right edge.
//
// That scoop is **not** a circle concentric with the disc, which is why an approximation reads
// wrong: Figma's own path arcs over the disc from a centre ~12px *above* it with a larger radius
// (~31 vs the disc's 27), so the gap is wide over the top and tucks in at the sides, and the
// bottom-right corner rounds tighter (~14px) than the other three (25px). The path below is
// Figma's, transformed out of the frame's 1.086× layout scale into intrinsic units.
//
// It is applied as a **fixed-size** mask layer anchored bottom-right, unioned with a plain layer
// covering the rest, so the scoop keeps its exact shape at any card width instead of stretching
// with the column.
$power-size: 54px;
$power-center-right: 80px;
$power-center-bottom: 21.5px;
/** Width and height of the scoop artwork; also how much of the card the second mask layer owns. */
$scoop-size: 150px;

// The card's box metrics, shared with the loading placeholder below. A shimmer that is a different
// size from the card it stands in for makes the whole grid jump when the first batch lands, so
// these live in one place and both rules read them.
$card-min-height: 150px;
$card-padding: 17px;

.card {
  position: relative;
  display: block;
  min-height: $card-min-height;
  padding: $card-padding;
  color: inherit;
  text-decoration: none;

  &:focus-visible {
    outline: none;
    border-radius: $radius-card;
    box-shadow: var(--focus-ring);
  }
}

// The card surface is painted on a pseudo-element rather than the element itself, because a
// `mask-image` on `.card` would clip the power disc (masks apply to descendants too).
.card::before {
  content: '';
  position: absolute;
  z-index: 0;
  inset: 0;
  border-radius: $radius-card;
  background: $color-white-400;
}

// Only a card that actually renders a power disc gets the cut-out — a device with no power
// control would otherwise show an empty bite taken out of its edge.
//
// Two mask layers, unioned: the scoop artwork pinned to the bottom-right at its natural size, and
// a plain rectangle covering everything to its left.
.notched::before {
  mask-image:
    url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="150" height="150.5" viewBox="0 0 150 150.5"><path fill="%23000" d="M0 0H150V150.5L136.03 150.5C120.34 150.5 106.58 140.04 102.39 124.93L101 119.92C100.69 118.79 100.53 118.22 100.46 117.97C91.37 87.18 47.66 87.46 38.96 118.36C38.89 118.62 38.74 119.19 38.44 120.32C38.34 120.68 38.29 120.86 38.25 121.02C33.55 138.3 17.92 150.35 0 150.49Z"/></svg>'),
    linear-gradient(#000, #000);
  mask-size:
    $scoop-size $scoop-size,
    calc(100% - #{$scoop-size}) 100%;
  mask-position:
    right bottom,
    left top;
  mask-repeat: no-repeat, no-repeat;
}

// The bottom wash, shown only while the power control is energised — the design draws a plain
// surface on every card whose disc is greyed.
.energised::before {
  // The raw literal here was `$color-accent1-400` (#F4FF79) written out by hand. Sampling the
  // design confirms the result is right — the wash lands on #F5F8DE over the #F6F7F7 surface — so
  // this is a token-hygiene change, not a visual one.
  background:
    linear-gradient(180deg, rgba($color-accent1-400, 0) 69.1%, rgba($color-accent1-400, 0.2) 100%),
    $color-white-400;
}

// Pending QC / Pending Configuration: visible in the grid, but inert and faded.
.disabledCard {
  cursor: default;
  opacity: 0.55;
  pointer-events: none;
}

// An expired subscription fades the identity block but keeps the card clickable.
.expiredCard {
  .category,
  .name {
    color: var(--text-disabled);
  }
}

.content {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.upper {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
}

.headerRow {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space-3);
  width: 100%;
}

.identity {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.category {
  margin: 0;
  font-size: $font-size-12;
  line-height: 1.4;
  color: var(--text-muted);
}

.categorySeparator {
  color: var(--text-muted);
}

.queuedTag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: 4px;
  font-weight: $font-weight-medium;
  color: $color-warning-900;
}

.queuedDot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: $color-warning-900;
}

.name {
  margin: 0;
  overflow: hidden;
  font-size: $font-size-18;
  line-height: 1.4;
  color: var(--text-primary);
  text-overflow: ellipsis;
  white-space: nowrap;
}

// Bound to Overlay/Black/40 in Figma, so the overlay token is the faithful mapping here even
// though it is being used as a text color.
.lastSync {
  display: inline-flex;
  flex: none;
  align-items: center;
  gap: 3px;
  margin: 0;
  font-size: $font-size-10;
  line-height: 1.4;
  color: var(--overlay-40);
  white-space: nowrap;
}

.fields {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  // The disc overlaps the card's bottom-right corner, so reserve room for it rather than letting
  // chips run underneath.
  padding-right: $scoop-size - $power-size;
}

.fieldChip {
  display: inline-flex;
  flex: 0 1 auto;
  // `justify-content: flex-start` (not center) so an over-long name truncates with a trailing
  // ellipsis instead of being clipped at both ends.
  align-items: center;
  justify-content: flex-start;
  overflow: hidden;
  min-width: 0;
  max-width: 120px;
  padding: 4px 6px;
  border: 1px solid var(--border-subtle);
  border-radius: $radius-md;
  background: var(--surface-card);
  font-size: $font-size-12;
  line-height: 1.4;
  color: var(--text-muted);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.power {
  position: absolute;
  z-index: 1;
  // Derived from the disc centre: 80px in from the right, 21.5px up from the bottom, so the
  // 54px disc overhangs the card's bottom edge by 5.5px.
  right: $power-center-right - 0.5 * $power-size;
  bottom: $power-center-bottom - 0.5 * $power-size;
}

// The loading placeholder. Same box as a real card — it renders in the same grid, so matching
// min-height and padding is all that's needed for the two to line up exactly and for nothing to
// shift when data arrives.
.skeleton {
  min-height: $card-min-height;
  padding: $card-padding;
  border-radius: $radius-card;
  background-color: $color-white-400;
  @include shimmer;
}
